import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template40: Página Aeropuertos de Tokio (Narita y Haneda)
// Basado en Airports.jsx de guia-tokio; todo el contenido viene de page (backend).
// Valores CSS en píxeles para evitar que MUI multiplique por 8.
const Template40 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [sticker1Error, setSticker1Error] = useState(false);
	const [sticker2Error, setSticker2Error] = useState(false);

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const headerText = page.texts?.[0]?.content;
	const introText = page.texts?.[1]?.content;
	const naritaTitle = page.texts?.[2]?.content;
	const naritaListHtml = page.texts?.[3]?.content;
	const hanedaTitle = page.texts?.[4]?.content;
	const hanedaListHtml = page.texts?.[5]?.content;
	const sabiasQueHtml = page.texts?.[6]?.content;
	const consejoTitle = page.texts?.[7]?.content;
	const consejoHtml = page.texts?.[8]?.content;

	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const sticker1 = getImageSrc("narita-sticker");
	const sticker2 = getImageSrc("taxi");

	const box = {
		backgroundColor: "#ffe3cc",
		border: "3px solid #f28b35",
		borderRadius: "16px",
		padding: "16px",
	};

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: "8px",
		marginTop: "0px",
		color: "#d57c31",
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: "0px",
	};

	const ul = {
		paddingLeft: "18px",
		margin: "0px",
		fontSize: "13px",
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
				"& ul": { paddingLeft: "18px", margin: "0px" },
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0px",
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: "0px",
						zIndex: 99999,
					}}
				>
					{headerText}
				</Box>
			)}

			{/* INTRO */}
			{introText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "12px 16px",
						fontSize: "13px",
						color: contentColor,
					}}
				>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}

			{/* BLOQUES */}
			<Box
				sx={{
					padding: "0px 16px 20px",
					display: "flex",
					flexDirection: "column",
					gap: "16px",
					position: "relative",
					marginTop: isSmallScreen ? "20px" : "0px",
				}}
			>
				{/* Narita */}
				{(naritaTitle || naritaListHtml) && (
					<Box data-block-id="text-2" sx={box}>
						{naritaTitle && (
							<Box component="h3" sx={h3}>
								{naritaTitle}
							</Box>
						)}
						{naritaListHtml && (
							<Box
								component="div"
								sx={{ "& ul": ul }}
								dangerouslySetInnerHTML={{ __html: naritaListHtml }}
							/>
						)}
					</Box>
				)}

				{/* Haneda */}
				{(hanedaTitle || hanedaListHtml) && (
					<Box data-block-id="text-3" sx={box}>
						{hanedaTitle && (
							<Box component="h3" sx={h3}>
								{hanedaTitle}
							</Box>
						)}
						{hanedaListHtml && (
							<Box
								component="div"
								sx={{ "& ul": ul }}
								dangerouslySetInnerHTML={{ __html: hanedaListHtml }}
							/>
						)}
					</Box>
				)}

				{/* SABÍAS QUE */}
				{sabiasQueHtml && (
					<Box
						data-block-id="text-4"
						sx={{
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "12px",
							padding: "12px 14px",
							fontSize: "12px",
							marginTop: "10px",
						}}
						dangerouslySetInnerHTML={{ __html: sabiasQueHtml }}
					/>
				)}

				{/* Stickers */}
				{sticker1 && !sticker1Error && (
					<Box
						component="img"
						src={sticker1}
						alt=""
						onError={() => setSticker1Error(true)}
						sx={{
							width: isSmallScreen ? "90px" : "100px",
							position: "absolute",
							top: isSmallScreen ? "-50px" : "120px",
							right: isSmallScreen ? "-20px" : "-10px",
						}}
					/>
				)}
				{sticker2 && !sticker2Error && (
					<Box
						component="img"
						src={sticker2}
						alt=""
						onError={() => setSticker2Error(true)}
						sx={{
							width: "100px",
							position: "absolute",
							bottom: isSmallScreen ? "-50px" : "60px",
							right: "-10px",
						}}
					/>
				)}

				{/* Consejo */}
				{(consejoTitle || consejoHtml) && (
					<Box
						data-block-id="text-5"
						sx={{
							backgroundColor: "#ffe3cc",
							border: "3px solid #f28b35",
							borderRadius: "16px",
							padding: "16px",
							fontSize: "12px",
							color: contentColor,
						}}
					>
						{consejoTitle && (
							<Box
								component="h4"
								sx={{
									marginTop: "0px",
									marginBottom: "8px",
									fontSize: "13px",
									color: "#d57c31",
									fontWeight: 700,
									textTransform: "uppercase",
								}}
							>
								{consejoTitle}
							</Box>
						)}
						{consejoHtml && (
							<Box
								component="p"
								sx={{
									...p,
									margin: "0px !important",
									marginBottom: "0px !important",
									"& a": {
										color: "#d57c31",
										textDecoration: "underline",
										fontWeight: "bold",
									},
								}}
								dangerouslySetInnerHTML={{ __html: consejoHtml }}
							/>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template40.thumbnail = "/images/template-1.png";

export default Template40;
